// Which action the editor's primary Shuffle FAB performs on tap.
//
// Extracted from UploadFloor's onClick so it can be tested directly. The branch
// order is behavior-preserving; only the shape changed.
//
// The ordering matters more than it looks. roomActive is 0 at editor open as a
// placeholder, not because room 0 is selected, and most readers in UploadFloor
// defend against that by pairing roomActive with lineBoundary. The first two
// branches here cannot: by the time a room is genuinely active the caller has
// already loaded it, so `roomActive !== -1` is the real signal. That makes it
// essential that anything which opens pick mode leaves roomActive at -1 —
// otherwise a picked room falls into SHUFFLE_ACTIVE_ROOM and shuffles
// roomsData[0] (or nothing at all) instead of the room the user pointed at.
// See the roomActive initializer in UploadFloor for the handoff case.

export const SHUFFLE_FAB_ACTION = {
  /** Active room, and a layout/filter context that means "re-furnish it". */
  FURNISH_ACTIVE_ROOM: "FURNISH_ACTIVE_ROOM",
  /** Active room → normal shuffle. */
  SHUFFLE_ACTIVE_ROOM: "SHUFFLE_ACTIVE_ROOM",
  /**
   * A room is selected (spotlight pick or canvas tap) but is not in roomsData
   * yet, so roomActive is still -1. Its type is known, so furnish it directly.
   */
  FURNISH_PICKED_ROOM: "FURNISH_PICKED_ROOM",
  /** Same as above, but the room has no type yet — ask before furnishing. */
  CHOOSE_ROOM_TYPE: "CHOOSE_ROOM_TYPE",
  /** Nothing selected → ask the user to tap a room. */
  ENTER_PICK_MODE: "ENTER_PICK_MODE",
};

/**
 * @param {object} p
 * @param {number}  p.roomActive        index into roomsData, or -1 for none
 * @param {string=} p.roomId            selected room's id, if any
 * @param {object=} p.shufflePickedRoom room picked via the spotlight flow
 * @param {boolean} p.lineBoundary      selected room's geometry is loaded
 * @param {object=} p.seeItAnotherWay   per-roomActive "another way" state
 * @param {boolean} p.openFilters       furniture filter panel is open
 * @param {object=} p.zonesRoom         roomId → chosen zones
 * @returns {string} one of SHUFFLE_FAB_ACTION
 */
export function resolveShuffleFabAction({
  roomActive,
  roomId,
  shufflePickedRoom,
  lineBoundary,
  seeItAnotherWay,
  openFilters,
  zonesRoom,
}) {
  if (roomActive !== -1 && (seeItAnotherWay?.[roomActive] || openFilters)) {
    return SHUFFLE_FAB_ACTION.FURNISH_ACTIVE_ROOM;
  }
  if (roomActive !== -1) {
    return SHUFFLE_FAB_ACTION.SHUFFLE_ACTIVE_ROOM;
  }
  if (roomId && (shufflePickedRoom != null || lineBoundary)) {
    return zonesRoom?.[roomId]?.length > 0
      ? SHUFFLE_FAB_ACTION.FURNISH_PICKED_ROOM
      : SHUFFLE_FAB_ACTION.CHOOSE_ROOM_TYPE;
  }
  return SHUFFLE_FAB_ACTION.ENTER_PICK_MODE;
}

/**
 * Whether the FAB row should stand the companion FABs down and leave the
 * primary one alone in the centre.
 *
 * The first three cases already behaved this way by unmounting the companions.
 * The fourth is the one this exists for: changing a furnished room's type (or
 * applying filters to it) flips the primary FAB's label to "Make Magic", and
 * until the user taps it nothing else in the row is worth offering.
 *
 * @param {object} p
 * @param {object=} p.shufflePickedRoom  room picked via the spotlight flow
 * @param {boolean} p.showZonesMenu      room-type dropdown is open
 * @param {boolean} p.openFilters        furniture filter panel is open
 * @param {number}  p.roomActive         index into roomsData, or -1 for none
 * @param {object=} p.seeItAnotherWay    per-roomActive "another way" state
 * @param {boolean} p.activeRoomHasFurniture  active room shows furniture today
 * @returns {boolean}
 */
export function isPrimaryFabSolo({
  shufflePickedRoom,
  showZonesMenu,
  openFilters,
  roomActive,
  seeItAnotherWay,
  activeRoomHasFurniture,
}) {
  if (shufflePickedRoom != null || showZonesMenu || openFilters) {
    return true;
  }
  // Requiring furniture is what holds this to a deliberate re-magic.
  // seeItAnotherWay is also true for an empty room — it is set from
  // !hasFurniture whenever a room is selected — and Add Items is the only
  // direct way to furnish one of those by hand, so an empty room keeps the
  // full row.
  return (
    roomActive !== -1 &&
    Boolean(seeItAnotherWay?.[roomActive]) &&
    Boolean(activeRoomHasFurniture)
  );
}

/**
 * How far the primary FAB must travel to reach the row's centre, as a
 * percentage of its own width.
 *
 * The row is a gapless flex row of equal `flex: 1` slots, so slot i spans
 * width/count and the first slot's centre sits at width/(2*count). Expressing
 * the delta against the FAB's own box keeps this a plain percentage translate,
 * which is what lets the move animate without touching layout.
 *
 * @param {number} fabCount number of FABs mounted in the row
 * @returns {number} percentage to translate the first FAB by
 */
export function primaryFabSoloShiftPercent(fabCount) {
  if (!Number.isFinite(fabCount) || fabCount <= 1) return 0;
  return ((fabCount - 1) / 2) * 100;
}
